import React, { ReactNode } from 'react';
import Image from '@atlaskit/image';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';

interface BadgeImageProps {
  src: string;
  alt: string;
}

interface CardWrapperProps {
  isClickable: boolean;
  children: ReactNode;
  to?: string;
}

export interface BadgeCardProps {
  image: BadgeImageProps;
  badgeTitle: string;
  link: string;
}

export const BadgeCard: React.FC<BadgeCardProps> = ({
  image,
  badgeTitle,
  link,
}) => {
  function truncateString(input: string) {
    if (input.length > 65) {
      return input.substring(0, 65) + '...';
    }
    return input;
  }

  const CardWrapper: React.FC<CardWrapperProps> = ({
    isClickable,
    children,
    to = '#',
  }) => {
    const onClickCard = () => {
      // Todo: Implement analytics
      // after https://bitbucket.org/atlassian/upp-ui/pull-requests/149/overview is merged
      window.open(to, '_blank');
    };

    return isClickable ? (
      <a css={containerStyles} onClick={onClickCard}>
        {children}
      </a>
    ) : (
      <div css={notClickableContainerStyles}>{children}</div>
    );
  };

  return (
    <CardWrapper isClickable={!!link} to={link}>
      <div css={imageContainerStyles}>
        <Image src={image.src} alt={image.alt} css={imageStyles} />
      </div>

      <div css={contentContainerStyles}>
        <p css={badgeTitleStyles}>{truncateString(badgeTitle)}</p>
      </div>
    </CardWrapper>
  );
};

const imageContainerStyles = css({
  flexShrink: 0,
  width: '65px',
  height: '65px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  borderRadius: '50%',
});

const imageStyles = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%',
});

const containerStyles = css({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '4px',
  padding: '16px 12px',
  boxShadow: token('elevation.shadow.raised'),
  textDecoration: 'none',
  color: 'inherit',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    cursor: 'pointer',
    boxShadow: token('elevation.shadow.overlay'),
    textDecoration: 'none',
    color: 'inherit',
  },

  '&:active': {
    border: `2px solid ${token('color.border.selected')}`,
  },
});

const notClickableContainerStyles = css({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '4px',
  padding: '16px 12px',
  border: `1px solid ${token('color.border')}`,
});

const contentContainerStyles = css({
  marginLeft: '16px',
});

const badgeTitleStyles = css({
  fontSize: '16px',
  fontFamily: token('font.family.brand.heading'),
  fontWeight: token('font.weight.semibold'),
  lineHeight: '24px',
  wordBreak: 'break-word',
  margin: 0,
});
